import moment from 'moment';
import { plural } from '@/shbdeployer/format';

/** Подписи действий и типов объектов для отображения в таблице. */
export const ACTION_LABELS = {
  login: 'Вход в систему',
  login_oauth: 'Вход через OAuth',
  logout: 'Выход из системы',
  create_admin: 'Создание администратора',
  create_backup: 'Создание резервной копии',
  restore_backup: 'Восстановление из резервной копии',
  snapshot_environments: 'Обновление снимков окружений',
  redeploy_stack: 'Повторное развертывание стека',
};

export const VERB_LABELS = {
  create: 'Создание',
  update: 'Изменение',
  delete: 'Удаление',
  start: 'Запуск',
  stop: 'Остановка',
  migrate: 'Перенос',
};

export const OBJECT_TYPE_LABELS = {
  session: 'сеанс',
  user: 'пользователь',
  user_password: 'пароль пользователя',
  access_token: 'токен доступа',
  team: 'команда',
  team_membership: 'членство в команде',
  role: 'роль',
  settings: 'настройки',
  ssl_settings: 'настройки SSL',
  environment: 'окружение',
  environment_group: 'группа окружений',
  environment_settings: 'настройки окружения',
  environment_registry: 'реестр окружения',
  environment_extension: 'расширение окружения',
  environment_association: 'привязка окружения',
  environment_dockerhub: 'учётные данные DockerHub окружения',
  environment_snapshot: 'снимок состояния окружения',
  environment_status: 'состояние окружения',
  docker_resource: 'ресурс Docker',
  kubernetes_resource: 'ресурс Kubernetes',
  azure_resource: 'ресурс Azure',
  agent_resource: 'ресурс агента',
  edge_resource: 'ресурс Edge',
  stack: 'стек',
  stack_git: 'Git-настройки стека',
  stack_webhook: 'вебхук стека',
  edge_stack: 'Edge-стек',
  edge_group: 'Edge-группа',
  edge_job: 'Edge-задание',
  custom_template: 'пользовательский шаблон',
  template: 'шаблон',
  registry: 'реестр образов',
  resource_control: 'права доступа к ресурсу',
  tag: 'тег',
  webhook: 'вебхук',
  backup: 'резервная копия',
  tls_certificate: 'TLS-сертификат',
  kubernetes: 'Kubernetes',
  ldap: 'LDAP',
  openamt: 'OpenAMT',
  fdo: 'FDO',
  audit_log: 'журнал действий',
  resource: 'ресурс',
};

export const RESULT_LABELS = {
  success: 'Успешно',
  failure: 'Ошибка',
  denied: 'Отказано',
};

export function describeAction(action) {
  if (ACTION_LABELS[action]) {
    return ACTION_LABELS[action];
  }
  const idx = action.indexOf('_');
  if (idx === -1) {
    return action;
  }
  const verb = action.slice(0, idx);
  const object = action.slice(idx + 1);
  const verbLabel = VERB_LABELS[verb];
  const objectLabel = OBJECT_TYPE_LABELS[object];
  if (verbLabel && objectLabel) {
    return `${verbLabel}: ${objectLabel}`;
  }
  return action;
}

const PERIODS = [
  { id: 'day', label: 'Последние 24 часа', from: () => moment().subtract(1, 'day') },
  { id: 'week', label: 'Последние 7 дней', from: () => moment().subtract(7, 'day') },
  { id: 'month', label: 'Последние 30 дней', from: () => moment().subtract(30, 'day') },
  { id: 'all', label: 'За всё время', from: () => null },
];

export default class AuditLogController {
  /* @ngInject */
  constructor($async, Notifications, AuditLogService) {
    this.$async = $async;
    this.Notifications = Notifications;
    this.AuditLogService = AuditLogService;

    this.periods = PERIODS;
    this.resultLabels = RESULT_LABELS;
    this.objectTypeLabels = OBJECT_TYPE_LABELS;
    this.describeAction = describeAction;

    this.filters = { search: '', username: '', action: '', objectType: '', result: '', period: 'week' };
    this.options = { usernames: [], actions: [], objectTypes: [], results: [] };
    this.state = { loading: false, page: 1, limit: 25, totalCount: 0, expanded: {} };
    this.logs = [];

    this.load = this.load.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);
  }

  queryParams() {
    const period = PERIODS.find((p) => p.id === this.filters.period) || PERIODS[1];
    const from = period.from();
    return {
      search: this.filters.search,
      username: this.filters.username,
      action: this.filters.action,
      objectType: this.filters.objectType,
      result: this.filters.result,
      from: from ? from.unix() : '',
      page: this.state.page,
      limit: this.state.limit,
    };
  }

  load() {
    return this.$async(async () => {
      this.state.loading = true;
      try {
        const data = await this.AuditLogService.logs(this.queryParams());
        this.logs = data.logs;
        this.state.totalCount = data.totalCount;
      } catch (err) {
        this.Notifications.error('Ошибка', err, 'Не удалось загрузить журнал действий');
      } finally {
        this.state.loading = false;
      }
    });
  }

  loadFilters() {
    return this.$async(async () => {
      try {
        this.options = await this.AuditLogService.filters();
      } catch (err) {
        this.Notifications.error('Ошибка', err, 'Не удалось загрузить значения фильтров');
      }
    });
  }

  onFilterChange() {
    this.state.page = 1;
    this.load();
  }

  resetFilters() {
    this.filters = { search: '', username: '', action: '', objectType: '', result: '', period: 'week' };
    this.onFilterChange();
  }

  changePage(page) {
    if (page < 1 || page > this.totalPages()) {
      return;
    }
    this.state.page = page;
    this.load();
  }

  totalPages() {
    return Math.max(1, Math.ceil(this.state.totalCount / this.state.limit));
  }

  toggle(entry) {
    this.state.expanded[entry.Id] = !this.state.expanded[entry.Id];
  }

  hasDetails(entry) {
    return entry.Before || entry.After || entry.Error;
  }

  resultBadge(result) {
    return { success: 'shb-badge--success', failure: 'shb-badge--error', denied: 'shb-badge--warning' }[result] || 'shb-badge--muted';
  }

  objectLabel(type) {
    return OBJECT_TYPE_LABELS[type] || type;
  }

  countLabel() {
    return `${this.state.totalCount} ${plural(this.state.totalCount, ['запись', 'записи', 'записей'])}`;
  }

  exportCsv() {
    return this.$async(async () => {
      try {
        await this.AuditLogService.exportCsv(this.queryParams());
      } catch (err) {
        this.Notifications.error('Ошибка', err, 'Не удалось экспортировать журнал действий');
      }
    });
  }

  $onInit() {
    this.loadFilters();
    this.load();
  }
}
